import { createListenerMiddleware } from '@reduxjs/toolkit';
import { monitoring } from 'common/services';
import { logger } from 'datadog-logs';
import { chooseDevices } from 'devices/core/selection';
import { MediaTrackState } from 'domain/Broadcast';
import { addAppConfirmationDialog } from 'modules/alerts/state';
import { RootState } from 'store';
import { agoraActions, selectAudioTrackState } from 'store/features/agora';
import { userMuted_WS } from 'store/features/event';

import { userActions } from './actions';
import { setAudioEnabled, setMediaSettings, setMicrophoneIssueAcknowledged } from './userSlice.actions';
import {
  findVideoInputDevices,
  persistVideoBackgroundSettings,
  readSavedDeviceChoices,
  saveDeviceChoice,
} from './utils';

export const userListener = createListenerMiddleware();

userListener.startListening({
  actionCreator: userMuted_WS,
  effect: ({ payload: { mutedByName } }, { dispatch }) => {
    dispatch(setAudioEnabled(false));
    dispatch(
      addAppConfirmationDialog({
        title: `You have been muted by ${mutedByName}`,
        description: 'You can unmute yourself by clicking the microphone below.',
        variant: 'confirmation',
        confirmLabel: 'Got it',
      })
    );
  },
});

userListener.startListening({
  actionCreator: userActions.mediaSettings.settingsUpdated,
  effect: (
    { payload: { backgroundImageId, blurLevel, audioInputDeviceId, audioOutputDeviceId, videoDeviceId } },
    { getOriginalState }
  ) => {
    const originalState = getOriginalState() as RootState;
    const previousSettings = originalState.user.mediaSettings;

    // Only store as "selected" if the device actually changed from previous value
    // This prevents marking all devices as user-selected when only one device is changed
    if (previousSettings.audioInputDeviceId !== audioInputDeviceId) saveDeviceChoice('microphone', audioInputDeviceId);
    if (previousSettings.audioOutputDeviceId !== audioOutputDeviceId) saveDeviceChoice('speaker', audioOutputDeviceId);
    if (previousSettings.videoDeviceId !== videoDeviceId) saveDeviceChoice('camera', videoDeviceId);

    persistVideoBackgroundSettings({ blurLevel, backgroundImageId });
  },
});

userListener.startListening({
  actionCreator: userActions.mediaSettings.deviceSelected,
  effect: ({ payload: { kind, deviceId } }) => saveDeviceChoice(kind, deviceId),
});

/**
 * An overload has to clear the persisted choice too, not just the slice. Otherwise the pre-overload
 * value is rehydrated on the next page load, re-applied to the published track, and overloads the
 * same device again — so an underpowered machine meets the warning on every join.
 */
userListener.startListening({
  actionCreator: userActions.mediaSettings.videoBackgroundShed,
  effect: () => {
    persistVideoBackgroundSettings({ blurLevel: 0, backgroundImageId: '' });
  },
});

/**
 * A choice made where there is no settings form to submit still has to outlive the page. Same storage
 * as `settingsUpdated` writes, so the two surfaces read each other's selection rather than each keeping
 * its own.
 */
userListener.startListening({
  actionCreator: userActions.mediaSettings.videoBackgroundSelected,
  effect: ({ payload: { blurLevel, backgroundImageId } }) => {
    persistVideoBackgroundSettings({ blurLevel, backgroundImageId });
  },
});

userListener.startListening({
  actionCreator: userActions.navigator.mediaDevices.devicesFound,
  effect: ({ payload: devices }, { dispatch, getOriginalState }) => {
    const originalState = getOriginalState() as RootState;
    const originalDevices = originalState.user.mediaDevices;
    const isInitialLoad = originalDevices.length === 0 && devices.length > 0;

    // Every change re-decides, so a headset the OS switches to is followed and an unplugged device
    // is replaced; the rules keep the user's own picks and never switch cameras by themselves.
    const choices = chooseDevices({
      cameras: findVideoInputDevices(devices),
      microphones: devices.filter((device) => device.kind === 'audioinput'),
      speakers: devices.filter((device) => device.kind === 'audiooutput'),
      currentCameraId: originalState.user.mediaSettings.videoDeviceId,
      saved: readSavedDeviceChoices(),
    });

    dispatch(
      setMediaSettings({
        audioInputDeviceId: choices.microphone.deviceId,
        audioOutputDeviceId: choices.speaker.deviceId,
        videoDeviceId: choices.camera.deviceId,
      })
    );

    monitoring.trackAction('devices.device_selection', {
      audioInput: choices.microphone,
      audioOutput: choices.speaker,
      video: choices.camera,
    });

    // Track not_found signals (only on initial load or when device disappears)
    const hasNoMicrophone = !devices.some((d) => d.kind === 'audioinput');
    const hasNoCamera = !devices.some((d) => d.kind === 'videoinput');
    const hasNoSpeaker = !devices.some((d) => d.kind === 'audiooutput');

    if (hasNoMicrophone) {
      monitoring.trackAction('devices.microphone.not_found');
    }
    if (hasNoCamera) {
      monitoring.trackAction('devices.camera.not_found');
    }
    if (hasNoSpeaker) {
      monitoring.trackAction('devices.speaker.not_found');
    }

    // Track not_found_recovered (device plugged in after being missing)
    const hadNoMicrophone = originalDevices.length > 0 && !originalDevices.some((d) => d.kind === 'audioinput');
    if (hadNoMicrophone && !hasNoMicrophone) {
      monitoring.trackAction('devices.microphone.not_found_recovered');
    }

    const hadNoCamera = originalDevices.length > 0 && !originalDevices.some((d) => d.kind === 'videoinput');
    if (hadNoCamera && !hasNoCamera) {
      monitoring.trackAction('devices.camera.not_found_recovered');
    }

    const hadNoSpeaker = originalDevices.length > 0 && !originalDevices.some((d) => d.kind === 'audiooutput');
    if (hadNoSpeaker && !hasNoSpeaker) {
      monitoring.trackAction('devices.speaker.not_found_recovered');
    }

    // Log device info
    logger.info('[MediaDevices] devices found', {
      mediaDevices: {
        devices,
        deviceCount: devices.length,
        audioInputCount: devices.filter((d) => d.kind === 'audioinput').length,
        audioOutputCount: devices.filter((d) => d.kind === 'audiooutput').length,
        videoInputCount: devices.filter((d) => d.kind === 'videoinput').length,
        isInitialLoad,
      },
    });
  },
});

userListener.startListening({
  actionCreator: agoraActions.audioTrack.toggling.completed,
  effect: (_, { dispatch, getState }) => {
    const audioTrackState = selectAudioTrackState(getState() as RootState);
    if (audioTrackState === MediaTrackState.Disabled) {
      dispatch(setMicrophoneIssueAcknowledged(false));
    }
  },
});
